import React, { useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { math } from 'polished';
import {
    DaInput,
    InternalGrid,
    DaSelect,
    ThemeDefault,
    ListIcon,
    SearchIcon,
} from '@paygreen/paygreen-ui';
import { NoDataFoundCard } from '../../../components';
import { IsMobile, IsFrench } from '../../../utils/tools';
import ProductCard from './ProductCard';
import ProductCardSkeletons from './ProductCardSkeletons';
import { ProductCardGrid } from './ProductCardSkeletons';

const CategoryProductForm = ({
    t,
    i18n,
    searchName,
    setSearchName,
    setPageIndex,
    searchCategory,
    setSearchCategory,
    setTempSelectedProduct,
    tempSelectedProduct,
    isLoading,
    productsData,
    productCategories,
    isMatchingSector,
}) => {
    const [categoriesOptions, setCategoriesOptions] = useState([]);

    useEffect(() => {
        const tempCategoriesOptions = [
            {
                value: '',
                text: t(
                    'matching.addManuallyForm.categorySearch.selectPlaceHolder',
                ),
            },
            ...productCategories?.map((category) => {
                if (isMatchingSector) {
                    return {
                        text: category[IsFrench(i18n, 'name')] || '',
                        value: category.idMonetaryRatioCategory || 0,
                    };
                }
                return {
                    text: category[IsFrench(i18n, 'monetaryRatioName')] || '',
                    value: category.idMonetaryRatio || 0,
                };
            }),
        ];
        setCategoriesOptions(tempCategoriesOptions);
    }, [productCategories, isMatchingSector, i18n, t]);

    // we build categories options based on categories tree received from API

    const handleSelectedProduct = ({
        emissionExternalId,
        idMonetaryRatio,
        value,
        unit,
        documentation,
        source,
        ...product
    }) => {
        setTempSelectedProduct({
            emissionExternalId: emissionExternalId,
            monetaryRatioMatch: idMonetaryRatio,
            emissionFactorValue: emissionExternalId ? value + unit : null,
            monetaryRatioValue: idMonetaryRatio ? value + 'kgCO2eq/€' : null,
            documentation: documentation,
            source: source,
            emissionFactorName: emissionExternalId
                ? product.attributeNameFrench
                    ? product[IsFrench(i18n, 'baseName')] +
                      ' - ' +
                      product[IsFrench(i18n, 'attributeName')]
                    : product[IsFrench(i18n, 'baseName')]
                : null,
            monetaryRatioName: idMonetaryRatio
                ? product[IsFrench(i18n, 'monetaryRatioName')]
                : null,
        });
    };

    return (
        <>
            <InternalGrid
                displayType="grid"
                gridTemplateColumns={IsMobile() ? '1fr' : '1fr 1fr'}
                alignItems="stretch"
                justifyItems="stretch"
                gridGap="md"
                hasStaticWidth={IsMobile()}
                childrenMarginBottom="md"
            >
                <DaInput
                    onChange={(e) => {
                        setPageIndex(0);
                        setSearchName(e.target.value);
                    }}
                    value={searchName}
                    name="equivalenceName"
                    id="equivalenceName"
                    type="text"
                    blockWidth="lg"
                    fieldSize="md"
                    placeholder={t(
                        'matching.addManuallyForm.categorySearch.searchPlaceholder',
                    )}
                    icon={<SearchIcon />}
                />

                {isMatchingSector && (
                    <DaSelect
                        options={categoriesOptions}
                        blockWidth="lg"
                        value={searchCategory}
                        fieldSize="md"
                        onChange={(e) => {
                            setPageIndex(0);
                            setSearchCategory(e.target.value);
                        }}
                        icon={<ListIcon />}
                    />
                )}
            </InternalGrid>

            <InternalGrid
                displayType="grid"
                gridTemplateColumns="1fr"
                gridTemplateRows="1fr"
                alignItems="stretch"
                justifyItems="stretch"
                style={{
                    // we add a min height that equals the height of skeletons inside the grid to guarantee a constant height displayed for the modal when searching
                    minHeight: `${math(
                        ThemeDefault.imageSize.xxs +
                            '*2 +' +
                            ThemeDefault.space.sm,
                    )}`,
                }}
            >
                {isLoading ? (
                    <ProductCardSkeletons blockWidth="md" />
                ) : productsData?.length ? (
                    <ProductCardGrid>
                        {productsData?.map((product, key) => (
                            <ProductCard
                                key={key}
                                onClick={() => handleSelectedProduct(product)}
                                product={product}
                                isSelected={
                                    tempSelectedProduct?.emissionExternalId
                                        ? product.emissionExternalId ===
                                          tempSelectedProduct?.emissionExternalId
                                        : false ||
                                          tempSelectedProduct?.monetaryRatioMatch
                                        ? parseInt(product.idMonetaryRatio) ===
                                          parseInt(
                                              tempSelectedProduct?.monetaryRatioMatch,
                                          )
                                        : false
                                }
                            />
                        ))}
                    </ProductCardGrid>
                ) : (
                    <NoDataFoundCard
                        blockWidth="none"
                        message={t('noDataFound')}
                    />
                )}
            </InternalGrid>
        </>
    );
};

CategoryProductForm.propTypes = {
    setSearchName: PropTypes.func.isRequired,
    setPageIndex: PropTypes.func.isRequired,
    setSearchCategory: PropTypes.func.isRequired,
    setTempSelectedProduct: PropTypes.func.isRequired,
    tempSelectedProduct: PropTypes.object,
    isLoading: PropTypes.bool,
    productsData: PropTypes.array,
};

CategoryProductForm.defaultProps = {
    isLoading: false,
    productsData: [],
};

export default withTranslation('common')(CategoryProductForm);
